// @ts-strict-ignore
import isBoolean from 'lodash/isBoolean';
import isNumber from 'lodash/isNumber';
import moment, {Moment} from 'moment';

import {DateRange, DateRangeAttributes} from 'common/widgets/date-range-picker/models/date-range';
import {
  END_OF_TODAY,
  END_OF_YESTERDAY,
  NOW,
  START_OF_QUARTER,
  START_OF_TODAY,
  START_OF_YEAR,
  START_OF_YESTERDAY,
  X_TO_DATE,
} from 'common/widgets/date-range-picker/models/presets';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {UNITS} from 'common/util/date';
import {TimeSection} from 'common/report/insights/models/section';
import {DATASET_MIXPANEL} from 'common/report/constants';
import {BaseDate, DateRangeType, TimeClause as BookmarkTimeClause, XAU_MATH_TYPES} from 'common/types/reports/bookmark';
import {reporter} from 'common/report/error-reporting';

import {DATE_RANGE_TYPE, RANGES, TimeClause} from '../models/time-clause';
import {TIME_UNITS} from '../models/constants';

interface TimeSectionParams {
  selectedTimeUnit?: TimeUnit;
  overrideTimeUnit?: TimeUnit;
  useDefaultTimeUnit?: boolean;
}

export function getBookmarkTimeClauseForDateRange(dateRange: DateRange): BookmarkTimeClause {
  // rewrite special presets
  const sinceStartOfToday =
    dateRange.type === DateRangeType.Since &&
    dateRange.from === START_OF_TODAY &&
    (!dateRange.to || dateRange.to === NOW);
  const betweenStartAndEndOfToday =
    dateRange.type === DateRangeType.Between && dateRange.from === START_OF_TODAY && dateRange.to === END_OF_TODAY;
  const periodToDate =
    dateRange.type === DateRangeType.Since &&
    X_TO_DATE.includes(dateRange.from as string) &&
    (!dateRange.to || dateRange.to === NOW);
  if (periodToDate) {
    let unit = TimeUnit.Day;
    if (dateRange.from === START_OF_QUARTER) {
      unit = TimeUnit.Week;
    } else if (dateRange.from === START_OF_YEAR) {
      unit = TimeUnit.Month;
    }
    return {
      dataset: DATASET_MIXPANEL,
      dateRangeType: DateRangeType.Since,
      value: [dateRange.from] as [BaseDate],
      window: null,
      unit,
    };
  } else if (sinceStartOfToday || betweenStartAndEndOfToday) {
    return {
      dataset: DATASET_MIXPANEL,
      dateRangeType: DateRangeType.Since,
      value: START_OF_TODAY,
      window: null,
      unit: TimeUnit.Hour,
    };
  } else if (
    dateRange.type === DateRangeType.Between &&
    dateRange.from === START_OF_YESTERDAY &&
    dateRange.to === END_OF_YESTERDAY
  ) {
    return {
      dataset: DATASET_MIXPANEL,
      dateRangeType: DateRangeType.Between,
      value: [START_OF_YESTERDAY, END_OF_YESTERDAY],
      window: null,
      unit: TimeUnit.Hour,
    };
  }

  const {type, from, to, window} = dateRange;
  const value = (() => {
    switch (type) {
      case DateRangeType.Since:
        return [from] as [BaseDate];
      case DateRangeType.On:
        return [from, from] as [BaseDate, BaseDate];
      case DateRangeType.Between:
        return [from, to] as [BaseDate, BaseDate];
    }
    return null;
  })();

  const unit = ((): TimeUnit => {
    if (type === DateRangeType.On) {
      return TimeUnit.Hour;
    }
    if (type === DateRangeType.Between || type === DateRangeType.Since) {
      if (dateRange.getIntervalDays() < 4) {
        return TimeUnit.Hour;
      } else if (dateRange.getIntervalDays() < 90) {
        return TimeUnit.Day;
      }
      return TimeUnit.Month;
    }

    if (type === DateRangeType.RelativeAfter) {
      // when a single unit is selected, avoid displaying
      // a single data point by breaking down to a smaller unit
      if (window.value === 1) {
        switch (window.unit) {
          case TimeUnit.Hour:
            return TimeUnit.Minute;
          case TimeUnit.Day:
            return TimeUnit.Hour;
          case TimeUnit.Week:
            return TimeUnit.Day;
          case TimeUnit.Month:
            return TimeUnit.Week;
        }
      }
      return window.unit;
    }
    return TimeUnit.Day;
  })();
  return {
    ...(isBoolean(dateRange.rolling) ? {rolling: dateRange.rolling} : {}),
    dataset: DATASET_MIXPANEL,
    dateRangeType: type,
    unit,
    value,
    ...(window ? {window} : {}),
  };
}

/** Transforms */
// TODO opslead
function dateRangeAttrsFromTimeClause(timeClause: TimeClause): DateRange {
  let options = {from: null, unit: null, to: null, value: null};
  let dateRangeType = timeClause.dateRangeType;
  if (
    timeClause.dateRangeType === DATE_RANGE_TYPE.RELATIVE ||
    (!timeClause.dateRangeType && isNumber(timeClause.value)) ||
    timeClause.range === RANGES.TODAY ||
    timeClause.range === RANGES.YESTERDAY
  ) {
    dateRangeType = DATE_RANGE_TYPE.RELATIVE;
    let optionsSource;
    if (timeClause.window) {
      // new relative window, or range
      optionsSource = timeClause.window;
    } else if (isNumber(timeClause.value)) {
      // old window format
      optionsSource = timeClause;
    } else {
      // current day, or yesterday
      optionsSource = {unit: timeClause.unit || TIME_UNITS.HOUR};
    }

    options.unit = optionsSource.unit;
    options.value = optionsSource.value;
  }

  if (Array.isArray(timeClause.value)) {
    const [from, to] = timeClause.value;
    options = {to, from, unit: null, value: null};
    if (!to) {
      dateRangeType = dateRangeType || DATE_RANGE_TYPE.SINCE;
    } else if (to === from) {
      dateRangeType = dateRangeType || DATE_RANGE_TYPE.ON;
    } else {
      dateRangeType = dateRangeType || DATE_RANGE_TYPE.BETWEEN;
    }
  }

  // const {unit, value} = options;
  const dateRangeAttributes = {type: dateRangeType, from: options.from, to: options.to, window: null};
  if (isBoolean(timeClause.rolling)) {
    dateRangeAttributes[`rolling`] = timeClause.rolling;
  }
  if (options.unit) {
    dateRangeAttributes.window = {unit: options.unit, value: options.value};
  }

  return new DateRange(dateRangeAttributes);
}

/**
 * Transforms TimeSection into DateRange
 */
export function getDateRange(timeClauses: TimeClause[]): DateRange {
  if (!timeClauses.length) {
    return new DateRange();
  }
  const clause = timeClauses[0];
  if (clause.range === RANGES.TODAY) {
    return new DateRange({
      type: DateRangeType.Between,
      from: START_OF_TODAY,
      to: END_OF_TODAY,
    });
  } else if (clause.range === RANGES.YESTERDAY) {
    return new DateRange({
      type: DateRangeType.Between,
      from: START_OF_YESTERDAY,
      to: END_OF_YESTERDAY,
    });
  }

  return dateRangeAttrsFromTimeClause(clause);
}

/**
 * Transforms DateRange into TimeSection
 */
export function getTimeSection(
  dateRange: Partial<DateRangeAttributes>,
  {selectedTimeUnit, overrideTimeUnit, useDefaultTimeUnit}: TimeSectionParams = {},
) {
  const rawTimeClause = getBookmarkTimeClauseForDateRange(new DateRange(dateRange));
  if (overrideTimeUnit) {
    rawTimeClause.unit = overrideTimeUnit;
  } else if (!useDefaultTimeUnit) {
    rawTimeClause.unit = selectedTimeUnit;
  }
  return new TimeSection([new TimeClause(rawTimeClause)]);
}

/**
 * For a given timeseries data point, calculates the relative date range for the XAU math operators
 */
export function timestampToXauDateRange(
  timestamp: number,
  {unit, operator, isLastInterval = false, lastIntervalToDate = null},
) {
  const MAU_DAYS = 30;
  const WAU_DAYS = 7;
  // These are the only units allowed in XAU queries, and the only ones this function handles
  const ALLOWED_UNITS = [UNITS[`day`], UNITS[`week`], UNITS[`month`]];
  if (!ALLOWED_UNITS.includes(unit)) {
    reporter.error(new Error(`Unit not in ${ALLOWED_UNITS}: "${unit}"`));
  }

  const mToDate = moment(lastIntervalToDate);
  const mIntervalStartDate = moment(timestamp);
  // Calculate the end date for the date range:
  // timestamp + 6 days (for week because range is inclusive) or end of month, unless > lastIntervalToDate
  let toDate: Moment;
  if (unit === UNITS[`month`]) {
    toDate = isLastInterval ? mToDate : moment(mIntervalStartDate).endOf(`month`);
  } else if (unit === UNITS[`week`]) {
    toDate = isLastInterval ? mToDate : moment(mIntervalStartDate).add(WAU_DAYS - 1, `days`);
  } else if (unit === UNITS[`day`]) {
    toDate = mIntervalStartDate;
  }

  let fromDate: Moment;
  if (operator === XAU_MATH_TYPES.MAU) {
    fromDate = moment(toDate).subtract(MAU_DAYS - 1, `days`);
  } else if (operator === XAU_MATH_TYPES.WAU) {
    fromDate = moment(toDate).subtract(WAU_DAYS - 1, `days`);
  }

  return {fromDate, toDate};
}
